import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { qualityApi, type QualityDocItem } from '../services/quality';

function chapterSortValue(chapter: string | null): number[] {
  return String(chapter ?? '')
    .split('.')
    .map((part) => Number.parseInt(part || '0', 10) || 0);
}

function compareChapter(a: string | null, b: string | null): number {
  const aa = chapterSortValue(a);
  const bb = chapterSortValue(b);
  const length = Math.max(aa.length, bb.length);
  for (let index = 0; index < length; index += 1) {
    const diff = (aa[index] ?? 0) - (bb[index] ?? 0);
    if (diff !== 0) {
      return diff;
    }
  }
  return 0;
}

function renderLines(text: string | null) {
  if (!text) {
    return null;
  }

  return text.split('\n').map((line, index) => (
    <p key={`${index}-${line}`} className="whitespace-pre-wrap text-[15px] leading-7 text-slate-700 print:text-black">
      {line}
    </p>
  ));
}

function PrintBlock({ title, text }: { title: string; text: string | null }) {
  if (!text) {
    return null;
  }

  return (
    <section className="rounded-[20px] border border-slate-300 bg-white p-4 print:rounded-none print:border-0 print:p-0">
      <h2 className="mb-3 text-lg font-semibold text-slate-950 print:text-black">{title}</h2>
      <div className="space-y-3">{renderLines(text)}</div>
    </section>
  );
}

export function QualityDocsPrintPage() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<QualityDocItem[]>([]);

  useEffect(() => {
    qualityApi.docs()
      .then((data) => setItems(data.items))
      .catch(() => toast.error('Kunde inte ladda utskriftsvyn'))
      .finally(() => setLoading(false));
  }, [toast]);

  const sortedItems = useMemo(
    () => [...items].sort((left, right) => compareChapter(left.chapter, right.chapter) || left.id - right.id),
    [items],
  );

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 print:hidden">
        <div className="space-y-3">
          <Link to="/quality/docs" className="inline-flex items-center rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm transition hover:border-slate-400 hover:bg-slate-50">
            Tillbaka
          </Link>
          <h1 className="text-[26px] font-semibold tracking-[-0.03em] text-slate-950 md:text-[30px]">Kvalitetspärm, skrivvy</h1>
        </div>

        <button type="button" className="btn btn-secondary rounded-2xl px-4 py-2" onClick={() => window.print()}>
          Skriv ut
        </button>
      </div>

      {loading ? (
        <div className="text-slate-500">Laddar dokument...</div>
      ) : (
        <div className="space-y-8 print:space-y-10">
          {sortedItems.map((item) => (
            <article key={item.id} className="space-y-4 break-inside-avoid">
              <header className="space-y-1">
                <div className="text-sm font-semibold uppercase tracking-[0.12em] text-slate-500 print:text-black">{item.chapter ?? '-'}</div>
                <h2 className="text-[24px] font-semibold tracking-[-0.03em] text-slate-950 print:text-black">{item.title}</h2>
              </header>

              <PrintBlock title="Inledning" text={item.intro} />
              <PrintBlock title="Beskrivning av arbetssätt" text={item.description} />
              <PrintBlock title="Lagstiftning" text={item.legislation} />
              <PrintBlock title="Branschens rekommendationer" text={item.recommendations} />
              <PrintBlock title="Faror" text={item.hazardSummary} />
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
